// Builds the iMessage body the user sends to Ori to verify. It doubles
// as a written consent record: it carries the 6-digit verification code
// plus an explicit confirmation of the user's 18+ status and acceptance
// of the Terms of Service and Privacy Policy.
//
// The acceptance date shown here is human-readable (the day the user taps
// Continue). The precise machine record is the ISO 8601 `tos_accepted_at`
// the form sends to the server — pass the same `acceptedAt` to both so
// the displayed date and the stored timestamp refer to the same moment.
//
// The 6-digit code is the only run of six consecutive digits in the
// message (the date carries separators), so the verify hook's code
// extractor (/(?<!\d)\d{6}(?!\d)/) still matches it cleanly.

function formatLongDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function buildVerifyMessage(opts: {
  code: string;
  /**
   * Whether to carry the 18+ / ToS attestation. New signups set this true
   * so the message records consent. The already-registered plan-join branch
   * leaves it false — the user is past the ToS collection step and the
   * verify text only needs the code to prove ownership of the handle.
   */
  attestAge?: boolean;
  /** Moment the user tapped Continue; defaults to now. */
  acceptedAt?: Date;
}): string {
  // Already-onboarded short form: no fresh ToS/age attestation needed,
  // the message just carries the code. Verify hook's extractor (the only
  // run of six consecutive digits in the body) still matches cleanly.
  if (!opts.attestAge) {
    return `Hi Ori — code ${opts.code}.`;
  }
  // Acceptance date: human-readable, in the user's local timezone.
  const accepted = formatLongDate(opts.acceptedAt ?? new Date());
  return (
    `Hi Ori — code ${opts.code}. I confirm I'm 18 or older, `
    + `and I accept your Terms of Service (${accepted}) and Privacy Policy.`
  );
}
